import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { listarContatos } from '../../clientes/api';
import { listarUsuarios } from '../../usuarios/api';
import { ActionModal } from './ActionModal';
import { useCriarInteracao } from '../hooks';
import type { Oportunidade, OpportunityInteractionTipo } from '../api';

const TIPO_OPTIONS: { value: OpportunityInteractionTipo; label: string }[] = [
  { value: 'ligacao', label: 'Ligação' },
  { value: 'reuniao', label: 'Reunião' },
  { value: 'email', label: 'E-mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'apresentacao', label: 'Apresentação' },
  { value: 'visita', label: 'Visita' },
  { value: 'envio_material', label: 'Envio de material' },
  { value: 'negociacao', label: 'Negociação' },
  { value: 'alinhamento_tecnico', label: 'Alinhamento técnico' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'outro', label: 'Outro' },
];

interface Props {
  opp: Oportunidade;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal de nova interação (US-COM-004 §Interações Comerciais,
 * task 15.3). Cria uma interação vinculada à oportunidade; o backend
 * reseta `is_stalled=false` na mesma transação — a UI reflete via
 * invalidação da detail query, tirando o badge "Parada" sem reload.
 *
 * Escopo intencional: só criação. Remoção acontece na lista de
 * interações e edição é imutável nesta primeira iteração
 * (registro histórico — spec §Preservação de Histórico).
 */
export const InteractionModal: React.FC<Props> = ({ opp, isOpen, onClose }) => {
  const [tipo, setTipo] = useState<OpportunityInteractionTipo | ''>('');
  const [occurredAt, setOccurredAt] = useState(
    new Date().toISOString().slice(0, 16),
  );
  const [contatoClienteId, setContatoClienteId] = useState('');
  const [summary, setSummary] = useState('');
  const [participantUserIds, setParticipantUserIds] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const mut = useCriarInteracao(opp.id);

  const { data: contatosResp } = useQuery({
    queryKey: ['picker', 'contatos', opp.clientId],
    queryFn: () => listarContatos(opp.clientId),
    staleTime: 30 * 1000,
    enabled: isOpen,
  });
  const contatoOptions = useMemo(
    () =>
      (contatosResp?.data ?? [])
        .filter((c) => c.status === 'ativo')
        .map((c) => ({ value: c.id, label: c.nome, description: c.email })),
    [contatosResp],
  );

  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
    enabled: isOpen,
  });
  const usuarios = usuariosResp?.data ?? [];

  const submit = async () => {
    setErr(null);
    if (!tipo || !occurredAt || summary.trim().length < 10) return;
    try {
      await mut.mutateAsync({
        tipo,
        occurredAt: new Date(occurredAt).toISOString(),
        contatoClienteId: contatoClienteId || undefined,
        summary: summary.trim(),
        participantUserIds:
          participantUserIds.length > 0 ? participantUserIds : undefined,
      });
      // Reset e fecha
      setTipo('');
      setOccurredAt(new Date().toISOString().slice(0, 16));
      setContatoClienteId('');
      setSummary('');
      setParticipantUserIds([]);
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao registrar interação.'));
    }
  };

  const canSubmit = !!tipo && !!occurredAt && summary.trim().length >= 10;

  const toggleParticipant = (userId: string) => {
    setParticipantUserIds((prev) =>
      prev.includes(userId) ? prev.filter((u) => u !== userId) : [...prev, userId],
    );
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Registrar interação"
      description="Interações contam como movimentação — a oportunidade sai automaticamente do estado 'parada' se estiver marcada como tal."
      submitLabel="Registrar"
      submitVariant="primary"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={canSubmit}
    >
      <div className="space-y-3">
        <AutocompleteSelect
          label="Tipo"
          options={TIPO_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={tipo}
          onChange={(v) => setTipo(v as OpportunityInteractionTipo | '')}
        />
        <TextInput
          label="Data e hora"
          type="datetime-local"
          value={occurredAt}
          onChange={(e) => setOccurredAt(e.target.value)}
        />
        <AutocompleteSelect
          label="Contato do cliente (opcional)"
          options={contatoOptions}
          value={contatoClienteId}
          onChange={setContatoClienteId}
        />
        <Textarea
          label="Resumo (mín. 10 caracteres)"
          rows={3}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          showCharCount
          minLength={10}
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Participantes internos (opcional)
          </label>
          <div className="max-h-40 overflow-y-auto rounded border border-gray-200 p-1">
            {usuarios.length === 0 && (
              <p className="p-2 text-xs text-gray-400">Carregando usuários…</p>
            )}
            {usuarios.map((u) => (
              <label
                key={u.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={participantUserIds.includes(u.id)}
                  onChange={() => toggleParticipant(u.id)}
                  className="h-3 w-3 rounded border-gray-300"
                />
                <span className="flex-1 truncate">{u.name}</span>
                <span className="text-xs text-gray-400">{u.email}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </ActionModal>
  );
};
